import { BodyText } from '@/components/BodyText';
import { InputBox } from '@/components/Inputs/InputBox';
import { NumberInput } from '@/components/Inputs/NumberInput';
import { MarkedSlider } from '@/components/MarkedSlider';
import { SearchPageObjectModel } from '@/poms/searchPageObjectModel';
import { InfoOutline } from '@mui/icons-material';
import { Box, IconButton, styled, Tooltip } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

const ThresholdContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 0 2px 0',
});

const HeaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 8px 0 0',
});

const SliderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  position: 'relative',
  gap: '12px',
  padding: '8px 8px 8px 14px',
});

export const ThresholdSliderSection = ({
  label,
  testId,
  tooltip,
  value,
  onChange,
}: {
  label: string;
  testId: string;
  tooltip: string;
  value: number;
  onChange: (value: number) => void;
}) => {
  const [localThreshold, setLocalThreshold] = useState(value);
  const debounceReference = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (value !== localThreshold && !debounceReference.current) {
      setLocalThreshold(value);
    }
  }, [value, localThreshold]);

  const debouncedOnChange = useCallback(
    (newValue: number) => {
      if (debounceReference.current) {
        clearTimeout(debounceReference.current);
      }
      debounceReference.current = setTimeout(() => {
        onChange(newValue);
      }, 300);
    },
    [onChange]
  );

  const handleThresholdChange = (newValue: number) => {
    setLocalThreshold(newValue);
    debouncedOnChange(newValue);
  };

  return (
    <ThresholdContainer data-testid={testId}>
      <HeaderContainer>
        <Box display='flex' alignItems='center' gap='2px'>
          <BodyText color='text.secondary'>{label}</BodyText>
          <Tooltip title={tooltip}>
            <IconButton
              size='small'
              data-testid={SearchPageObjectModel.ThresholdTooltip}
              sx={{ padding: '2px', minWidth: '16px', minHeight: '16px', color: 'text.secondary' }}
            >
              <InfoOutline sx={{ fontSize: '14px' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </HeaderContainer>
      <InputBox>
        <SliderContainer>
          <MarkedSlider
            sx={{
              minWidth: '120px',
              '& .MuiSlider-mark': { backgroundColor: 'secondary.main' },
              '& .MuiSlider-track': { backgroundColor: 'secondary.main' },
              '& .MuiSlider-rail': { backgroundColor: 'divider' },
            }}
            min={0}
            max={100}
            value={localThreshold}
            onChange={(_, newValue) => handleThresholdChange(newValue as number)}
            valueLabelDisplay='off'
          />
          <NumberInput
            value={localThreshold}
            onChange={handleThresholdChange}
            min={0}
            max={100}
            decimalPlaces={2}
            slotProps={{ input: { endAdornment: <BodyText>%</BodyText>, style: { fontSize: '14px' } } }}
          />
        </SliderContainer>
      </InputBox>
    </ThresholdContainer>
  );
};
